<template>
    <div class="d-flex"
         :style="{'justify-content': justifyContent, 'text-align': renderer.align}">
        <div class="py-1 mb-2" :style="{width: width}">
            <div :class="{row: horizontal}" class="w-100 m-0">
                <label v-if="field.label"
                       :class="horizontal ? 'col-4 align-self-center pl-0 pr-1' : 'py-0'"
                       class="col-form-label col-form-label-sm mb-0">
                    {{ label }}
                </label>
                <div :class="renderer.horizontal ? 'col-8 pl-1 pr-0' : 'mb-1'">
                    <DocumentViewerFieldEditor
                        :field="field"
                        :value="value"
                        :readonly="isReadonly"
                        @change="onChange"/>
                </div>
            </div>
            <template v-if="failedValidations.length !== 0 && ((metadata && metadata.touched) || isTouchedForm)">
                <FieldValidationMessage
                    v-for="validation in failedValidations"
                    :key="validation.key"
                    :validation="validation"/>
            </template>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import {DOC_CLEAR_DEPENDENT_FIELD_VALUES, DOC_FIELD_RUN_ACTIONS, DOC_FIELD_UPDATE} from '@/store/modules/doc-viewer'
import {FieldRendererAlign} from '@/types/document-viewer/field/modules/renderer'
import DocumentViewerFieldEditor from './DocumentViewerFieldEditor'
import FieldValidationMessage from './validation/FieldValidationMessage'
import {evaluateTeamLangPlaceholder} from '@/views/settings/team/lang'
import {checkIsReadonlyFieldInAccordanceWithRules} from '@/types/document-viewer/field/modules/rules'

export default {
    name: 'DocumentViewerField',
    components: {
        FieldValidationMessage,
        DocumentViewerFieldEditor
    },
    props: {
        field: {} // see FormField
    },
    computed: {
        ...mapGetters('doc_viewer', [
            'getFormFieldValue',
            'docWithNormalizedParts',
            'disabledDocument'
        ]),

        ...mapGetters('doc_viewer_settings', [
            'fields'
        ]),

        ...mapGetters('doc_viewer_metadata', [
            'isTouchedForm',
            'getFieldMetadata',
            'getFieldFailedValidations'
        ]),

        value() {
            return this.getFormFieldValue({path: this.field.path})
        },

        label() {
            return evaluateTeamLangPlaceholder(this.field.label)
        },

        renderer() {
            return this.field.renderer || {}
        },

        section() {
            return this.field.section || {}
        },

        editor() {
            return this.field.editor || {}
        },

        horizontal() {
            return this.renderer.horizontal
        },

        justifyContent() {
            if (this.renderer.align === FieldRendererAlign.RIGHT) {
                return 'flex-end'
            } else if (this.renderer.align === FieldRendererAlign.CENTER) {
                return 'center'
            }
            return 'flex-start'
        },

        width() {
            if (this.renderer.width) {
                if (_.isNumber(this.renderer.width)) {
                    return `${this.renderer.width}px`
                } else if (_.isString(this.renderer.width)) {
                    return this.renderer.width
                }
            }
            return '100%'
        },

        metadata() {
            return this.getFieldMetadata({path: this.field.path})
        },

        failedValidations() {
            return this.getFieldFailedValidations({path: this.field.path})
        },

        isReadonly() {
            return (this.field.hasOwnProperty('editable') && !this.field.editable) ||
                (this.section.hasOwnProperty('editable') && !this.section.editable) ||
                this.disabledDocument ||
                checkIsReadonlyFieldInAccordanceWithRules({
                    field: this.field,
                    doc: this.docWithNormalizedParts,
                    fields: this.fields
                })
        }
    },
    methods: {
        ...mapActions('doc_viewer', {
            updateField: DOC_FIELD_UPDATE,
            runFieldActions: DOC_FIELD_RUN_ACTIONS,
            clearDependentFieldValues: DOC_CLEAR_DEPENDENT_FIELD_VALUES
        }),

        onChange(data) {
            this.updateField({path: this.field.path, data})
            this.runFieldActions({path: this.field.path})
            this.clearDependentFieldValues({path: this.field.path})
        }
    },
    created() {
        const hasDefaultValue = typeof this.editor.default !== 'undefined' && this.editor.default !== null
        if (hasDefaultValue && (this.editor.forceOverrideWithDefault || !this.value)) {
            this.onChange(this.editor.default)
        }
    }
}
</script>

<style scoped lang="scss">
label {
  color: #2c3e50;
  margin-bottom: 2px;
}
</style>
